import {useRef, useState} from 'react'
import {mmap} from '@freckle/maybe'
import {fromMaybe} from '@freckle/maybe'
import {useSafeEffectExtraDeps} from '@freckle/react-hooks'
import {staticExtra} from '@freckle/educator-entities/ts/common/helpers/extra-deps'
import {CenteredSpinner} from '@freckle/educator-materials/ts/components/spinner'
import {getConfig} from '@freckle/classroom/ts/common/marketo-config'
import {marketoFormClass, marketoDivClass} from './marketo-form.module.scss'

const {
  MARKETO_SCRIPT_SRC,
  MARKETO_BASE_URI,
  MARKETO_MUNCHKIN_ID,
  MARKETO_QUOTE_REQUEST_FORM_ID_BY_COUNTRY,
} = getConfig()

export const MarketoForm = (props: {
  countryCode: string | null | undefined
  onSuccess: (a: {mktoFormId: string; followUpUrl: string}) => void
}) => {
  const {onSuccess, countryCode} = props

  const [isLoading, setIsLoading] = useState(true)
  const marketoFormRef = useRef(null)

  const formId = fromMaybe(
    () => MARKETO_QUOTE_REQUEST_FORM_ID_BY_COUNTRY.DEFAULT,
    mmap(countryCode => MARKETO_QUOTE_REQUEST_FORM_ID_BY_COUNTRY[countryCode], countryCode),
  )

  useSafeEffectExtraDeps<{
    onSuccess: (a: {mktoFormId: string; followUpUrl: string}) => void
  }>(
    ({onSuccess}) => {
      const isMarketoLoaded = () => typeof MktoForms2 !== 'undefined'

      if (!isMarketoLoaded()) {
        const script = document.createElement('script')
        script.src = MARKETO_SCRIPT_SRC
        document.body.appendChild(script)
      }

      //Poll for the Marketo script to finish loading
      const loadForm = setInterval(() => {
        if (isMarketoLoaded()) {
          clearInterval(loadForm)

          //API from: https://developers.marketo.com/javascript-api/forms/api-reference/
          MktoForms2.onFormRender(() => setIsLoading(false))
          MktoForms2.loadForm(
            MARKETO_BASE_URI,
            MARKETO_MUNCHKIN_ID,
            formId,
            (form: {
              render: (arg: unknown) => void
              onSuccess: (callback: (values: unknown, followUpUrl: string) => boolean) => void
              getId: () => string
            }) => {
              form.render(MktoForms2.$(marketoFormRef.current))
              form.onSuccess((_values: unknown, followUpUrl: string) => {
                onSuccess({mktoFormId: form.getId(), followUpUrl})
                //This will prevent the form from redirecting
                return false
              })
            },
          )
        }
      }, 100)

      return () => clearInterval(loadForm)
    },
    [formId],
    {onSuccess: staticExtra(onSuccess)},
  )

  return (
    //Hard coding these classnames for use by the Marketto css
    <div className={`${marketoDivClass} form-container freckle-form freckle-col-2`}>
      <form
        className={marketoFormClass}
        ref={marketoFormRef}
        data-test={`marketo-form-${isLoading ? 'loading' : 'loaded'}`}
      >
        {isLoading ? <CenteredSpinner /> : null}
      </form>
    </div>
  )
}
